/**
 * The three ways Test Connection dials (moved out of `NewConnectionPopup`, C0): the Azure Storage per-service probe
 * ladder, the SSH step-by-step test, and a plain connect. Each publishes its answer under the run's snapshot only while
 * the run is still current, and says whether the connection works.
 */
import type { Connection } from '@/types/electron';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import { storageEnabledServices } from '../../components/azureStorageFormRules';
import { storageTestOutcome } from '../../utils/storageTestLegs';
import { connectionTargetLine } from '../../utils/connectionServerLine';
import type { ConnectionTestResult, ConnectionTestSnapshot, useConnectionTestOutcome } from '../../hooks/useConnectionTestOutcome';
import type { ConnectionForm } from '../state/connectionForm';

type Tests = ReturnType<typeof useConnectionTestOutcome>;

export type DialContext = {
  api: NonNullable<typeof window.electronAPI>;
  /**
   * The form AS TYPED when the run started (what `snapshot` fingerprints). Never the normalized values a Save dials with:
   * a result snapshot built from trimmed values would not match the untrimmed form, and the Save would be refused as
   * "changed while tested" (C0 review C0-01). Normalizing is for dialing (`connection`) and persisting only.
   */
  formValues: ConnectionFormValues;
  connection: Connection;
  isCurrent: () => boolean;
  tests: Tests;
  snapshot: ConnectionTestSnapshot;
  track: TrackFunction;
};

/** What a storage run dialed, including its own edit (the failed services unticked). */
export type StorageDial = { success: boolean; values?: ConnectionFormValues; snapshot?: ConnectionTestSnapshot };

export async function dialStorage(
  context: DialContext,
  form: ConnectionForm,
  programmatic: (edit: () => void) => void,
): Promise<StorageDial> {
  const { formValues, connection, isCurrent, tests, track } = context;
  // Per-service probe ladder, not a connect: `testServices` registers nothing and each enabled service gets its own row.
  const response = await context.api.azureStorage.testServices({ config: connection });
  if (!response.ok) {
    // The structured error travels with the answer: a refusal of the settings withholds Save anyway (C2 review C2-02).
    const storageFailures = [{ category: response.error.category, ...(response.error.code ? { code: response.error.code } : {}), ...(response.error.fix ? { fixKind: response.error.fix.kind } : {}) }];
    if (isCurrent()) tests.show(context.snapshot, { result: { success: false, message: response.error.message, connection }, storageFailures, connection });
    return { success: false };
  }
  const outcome = storageTestOutcome(response.value);
  if (!isCurrent()) return { success: outcome.success };
  let tested = context.snapshot;
  let testedValues: ConnectionFormValues | undefined;
  // A FAILED service is unticked (re-tickable after a fix); a skipped one stays — its first listing is the test. Only
  // when ≥ 1 service verified (unticking everything would lock the retry), and only while the form still holds the
  // tested values: an account edited during the run is a different connection.
  if (outcome.success && outcome.failed.length > 0 && tests.matchesLive(tested)) {
    const remaining = storageEnabledServices(formValues).filter((service) => !outcome.failed.includes(service));
    programmatic(() => form.setFieldValue('azureStorageServices', remaining));
    // Only the service change is overlaid on the form as typed: the snapshot must fingerprint what stays on screen.
    testedValues = { ...formValues, azureStorageServices: remaining };
    tested = tests.snapshotOf(testedValues);
  }
  tests.show(tested, {
    legs: outcome.legs,
    legsSummary: outcome.summary,
    storageFailures: outcome.failures,
    connection,
    ...(outcome.success ? {} : { result: { success: false, variant: 'warning', message: outcome.message } }),
  });
  track('feature:storage_connect_probe', {
    services: outcome.services,
    scope: response.value.scope,
    dataDenied: outcome.dataDenied.length, // services that listed but could not read data (RBAC fix 2026-09-25)
  });
  return { success: outcome.success, values: testedValues, snapshot: tested };
}

const sshStatus = (status: 'ok' | 'error' | 'skipped' | 'not-attempted' | undefined): 'ok' | 'failed' | 'skipped' =>
  status === 'ok' ? 'ok' : status === 'error' ? 'failed' : 'skipped';

export async function dialStepwise(context: DialContext): Promise<boolean> {
  const result = await context.api.testConnectionStepwise(context.connection);
  if (context.isCurrent()) {
    context.tests.show(context.snapshot, { stepwise: result, connection: context.connection });
    // Low-cardinality {ok|failed|skipped}: never SSH error categories or detail strings.
    context.track('feature:ssh_test_completed', { ssh_status: sshStatus(result.ssh?.status), db_status: sshStatus(result.db.status) });
  }
  return result.db.status === 'ok';
}

export async function dialConnect(context: DialContext): Promise<boolean> {
  const { connection } = context;
  const result = await context.api.connectToDatabase(connection);
  if (result.success && connection.id) {
    // Fire-and-forget: tearing the probe down can hang for ~30 s, and the answer is already known.
    void Promise.resolve(context.api.disconnectFromDatabase(connection.id)).catch(() => {});
  }
  const shown: ConnectionTestResult = {
    success: result.success,
    // C2: say where it connected ("Connected to db.example:5432", the TNS alias, the file), not a bare "successful".
    message: result.success ? `Connected to ${connectionTargetLine(connection)}` : (result.error || 'Unknown error'),
    connection: result.success ? undefined : connection,
    errorCode: result.success ? undefined : result.errorCode,
  };
  if (context.isCurrent()) context.tests.show(context.snapshot, { result: shown });
  return result.success;
}
